import logger from '../../utils/logger';
import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PageLayout from '../PageLayout';

// Import step components
import PersonalInfoStep from './steps/PersonalInfoStep';
import LegalInsuranceStep from './steps/LegalInsuranceStep';
import ExperienceStep from './steps/ExperienceStep';
import AvailabilityStep from './steps/AvailabilityStep';
import PreferencesStep from './steps/PreferencesStep';
import PaymentLegalStep from './steps/PaymentLegalStep';
import MobileDebugger from '../debug/MobileDebugger';
import ErrorBoundary from '../ErrorBoundary';

const steps = [
  {
    label: 'Create Account',
    component: PersonalInfoStep,
    description: 'Create your account with basic contact information'
  },
  {
    label: 'Legal & Insurance',
    component: LegalInsuranceStep,
    description: 'Work authorization, background check, and insurance details'
  },
  {
    label: 'Experience & Services',
    component: ExperienceStep,
    description: 'Professional experience and services you provide'
  },
  {
    label: 'Availability & Transport',
    component: AvailabilityStep,
    description: 'Work schedule, availability, and transportation details'
  },
  {
    label: 'Preferences & Contact',
    component: PreferencesStep,
    description: 'Service preferences and emergency contact information'
  },
  {
    label: 'Payment & Agreement',
    component: PaymentLegalStep,
    description: 'Payment details and legal agreements'
  }
];

// Helper function to get appropriate steps based on user authentication status
const getStepsForUser = (user) => {
  if (user) {
    // Return steps without account creation for authenticated users
    return steps.slice(1);
  }
  // Return all steps for guest users
  return steps;
};

const MaidOnboardingWizard = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1: User Account Creation
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    
    // Step 2+: Profile Information  
    dateOfBirth: null,
    residentialAddress: '',
    
    // Legal & Insurance
    workAuthorized: null,
    backgroundCheckConsent: null,
    idUploadLink: '',
    hasInsurance: null,
    insuranceProvider: '',
    policyNumber: '',
    coiUploadLink: '',
    
    // Experience & Services
    yearsOfExperience: '',
    experienceDescription: '',
    servicesOffered: [],
    maxTravelDistance: 15,
    hasReliableTransportation: null,
    languages: [],
    
    // Availability & Transportation
    weeklyAvailability: {},
    preferredTimeSlots: [],
    hoursPerWeek: '',
    noticeRequired: '',
    emergencyAvailable: false,
    weekendAvailable: false,
    hasVehicle: null,
    vehicleType: '',
    canTransportEquipment: false,
    hasCleaningSupplies: null,
    
    // Preferences & Emergency Contact
    clientTypePreferences: [],
    preferredHourlyRate: 25,
    comfortableWithPets: false,
    willingToWorkInTeams: false,
    specialSkills: '',
    preferredCommunicationMethod: '',
    receiveSmsNotifications: false,
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    emergencyContactAltPhone: '',
    emergencyContactAddress: '',
    
    // Payment & Legal
    paymentMethod: '',
    bankName: '',
    accountType: '',
    taxClassification: '',
    // Legal agreements
    termsOfService: false,
    privacyPolicy: false,
    safetyGuidelines: false,
    qualityStandards: false,
    marketingCommunications: false,
    referralProgram: false,
    finalAcknowledgment: false
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [accountProcessingStep, setAccountProcessingStep] = useState('');
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasAttemptedNext, setHasAttemptedNext] = useState(false);
  
  // Auto-save and draft management
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedDraftId, setSavedDraftId] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Admin mode support
  const [searchParams] = useSearchParams();
  const targetMaidId = searchParams.get('maidId');
  const isAdminMode = !!targetMaidId;
  const [targetMaidInfo, setTargetMaidInfo] = useState(null);
  
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function to format validation errors
  const formatValidationErrors = (errorResponse) => {
    try {
      // If the error response has details array, format it nicely
      if (errorResponse.details && Array.isArray(errorResponse.details)) {
        const errorMessages = errorResponse.details.map(detail => {
          const fieldName = detail.path ? detail.path.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^\w/, c => c.toUpperCase()) : 'Field';
          return `• ${fieldName}: ${detail.msg}`;
        });
        return `Validation failed:\n${errorMessages.join('\n')}`;
      }
      // If it's just a string error, return as is
      return errorResponse.error || errorResponse.message || 'Validation failed';
    } catch (e) {
      return errorResponse.error || errorResponse.message || 'Validation failed';
    }
  };

  // Load existing draft on component mount
  const loadSavedData = useCallback(async () => {
    if (!user) return;
    
    try {
      // Build API URL with optional maidId parameter for admin mode
      const apiUrl = new URL(`${process.env.REACT_APP_API_URL}/api/maids/onboarding`);
      if (targetMaidId) {
        apiUrl.searchParams.append('maidId', targetMaidId);
      }
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const responseData = await response.json();
      
      if (response.ok && responseData.success && responseData.data) {
        // Load saved form data
        setFormData(responseData.data);
        
        // Store target maid info for admin mode
        if (responseData.maidProfile) {
          setTargetMaidInfo(responseData.maidProfile);
        }
        
        // Load draft info
        const draftInfo = responseData.draftInfo;
        if (draftInfo && draftInfo.isDraft) {
          setSavedDraftId('existing');
          setLastSaved(new Date(draftInfo.lastSavedAt));
          setActiveStep(Math.min(draftInfo.lastCompletedStep + 1, 5)); // Don't go past final step
        } else {
          // Start at step 1 for authenticated users or admin mode (skip account creation)
          setActiveStep(isAdminMode ? 1 : 1);
        }
      } else {
        // No existing data - start at step 1 (skip account creation) for authenticated users
        setActiveStep(isAdminMode ? 1 : 1);
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
      // Default to step 1 for authenticated users even on error
      setActiveStep(1);
    } finally {
      setIsLoaded(true);
    }
  }, [user, targetMaidId, isAdminMode]);

  // Auto-save progress
  const saveProgress = useCallback(async (stepNumber) => {
    setIsSaving(true);
    logger.log('Saving progress for step:', stepNumber, 'User authenticated:', !!user);
    
    try {
      // Filter out null/undefined values for draft submissions to avoid validation errors
      const cleanedFormData = Object.fromEntries(
        Object.entries(formData).filter(([key, value]) => 
          (value !== null && value !== undefined && value !== '' && value !== false) || 
          typeof value === 'number' ||
          key === 'maxTravelDistance' // Always include maxTravelDistance even if 0
        )
      );
      
      const submissionData = {
        ...cleanedFormData,
        currentStep: stepNumber,
        finalAcknowledgment: false, // Mark as draft
        // Multi-tenant fields
        tenantId: process.env.REACT_APP_TENANT_ID || 1,
        cityCode: process.env.REACT_APP_CITY_CODE || 'MCO',
        
        // Admin mode: include target maid ID if editing another maid's profile
        ...(targetMaidId && { targetMaidId }),
        
        // Note: fullLegalName, phoneNumber, email will be handled by backend
        // and stored in User table, while other fields go to MaidProfiles table
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/maids/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submissionData)
      });
      
      const responseData = await response.json();
      
      if (response.ok && responseData.success) {
        setSavedDraftId(responseData.data.maidProfileId);
        setLastSaved(new Date());
        logger.log('Progress saved successfully for step:', stepNumber);
        return { success: true };
      } else {
        console.error('Failed to save progress:', responseData);
        const formattedError = formatValidationErrors(responseData);
        return { success: false, error: formattedError };
      }
    } catch (error) {
      console.error('Error saving progress:', error);
      return { success: false, error: 'Network error while saving progress' };
    } finally {
      setIsSaving(false);
    }
  }, [formData, user]);

  const updateFormData = useCallback((stepData) => {
    setFormData(prev => ({
      ...prev,
      ...stepData
    }));
    // Mark that user has interacted with the form
    setHasUserInteracted(true);
    // Clear any step-specific errors
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(stepData).forEach(key => {
        delete newErrors[key];
      });
      return newErrors;
    });
  }, []);

  const validateStep = useCallback((stepIndex, shouldSetErrors = true) => {
    const stepErrors = {};
    
    switch (stepIndex) {
      case 0: // User Account Creation
        if (!formData.firstName?.trim()) stepErrors.firstName = 'First name is required';
        if (!formData.lastName?.trim()) stepErrors.lastName = 'Last name is required';
        if (!formData.email?.trim()) stepErrors.email = 'Email is required';
        if (!formData.password?.trim()) stepErrors.password = 'Password is required';
        if (formData.password && formData.password.length < 8) stepErrors.password = 'Password must be at least 8 characters long';
        if (!formData.phoneNumber?.trim()) stepErrors.phoneNumber = 'Phone number is required';
        
        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) stepErrors.email = 'Please enter a valid email address';
        
        // Phone number validation (should be formatted)
        const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
        if (formData.phoneNumber && !phoneRegex.test(formData.phoneNumber)) stepErrors.phoneNumber = 'Please enter a valid phone number';
        break;
        
      case 1: // Legal & Insurance
        if (!formData.residentialAddress?.trim()) stepErrors.residentialAddress = 'Residential address is required';
        if (formData.workAuthorized === null) stepErrors.workAuthorized = 'Work authorization is required';
        if (formData.backgroundCheckConsent === null) stepErrors.backgroundCheckConsent = 'Background check consent is required';
        if (formData.hasInsurance === null) stepErrors.hasInsurance = 'Insurance information is required';
        if (formData.hasInsurance && !formData.insuranceProvider?.trim()) stepErrors.insuranceProvider = 'Insurance provider is required';
        if (formData.hasInsurance && !formData.policyNumber?.trim()) stepErrors.policyNumber = 'Policy number is required';
        break;
        
      case 2: // Experience & Services
        if (!formData.yearsOfExperience || formData.yearsOfExperience < 0) stepErrors.yearsOfExperience = 'Years of experience is required';
        if (formData.yearsOfExperience && !Number.isInteger(Number(formData.yearsOfExperience))) stepErrors.yearsOfExperience = 'Years of experience must be a whole number';
        if (!formData.servicesOffered?.length) stepErrors.servicesOffered = 'At least one service is required';
        if (formData.hasReliableTransportation === null) stepErrors.hasReliableTransportation = 'Transportation information is required';
        break;
        
      case 3: // Availability & Transportation
        const availableDaysCount = Object.values(formData.weeklyAvailability || {}).filter(Boolean).length;
        if (availableDaysCount === 0) stepErrors.weeklyAvailability = 'At least one available day is required';
        if (formData.hasVehicle === null) stepErrors.hasVehicle = 'Vehicle information is required';
        if (formData.hasCleaningSupplies === null) stepErrors.hasCleaningSupplies = 'Cleaning supplies information is required';
        break;
        
      case 4: // Preferences & Emergency Contact
        if (!formData.emergencyContactName?.trim()) stepErrors.emergencyContactName = 'Emergency contact name is required';
        if (!formData.emergencyContactRelationship?.trim()) stepErrors.emergencyContactRelationship = 'Emergency contact relationship is required';
        if (!formData.emergencyContactPhone?.trim()) stepErrors.emergencyContactPhone = 'Emergency contact phone is required';
        break;
        
      case 5: // Payment & Legal
        if (!formData.paymentMethod?.trim()) stepErrors.paymentMethod = 'Payment method is required';
        if (!formData.taxClassification?.trim()) stepErrors.taxClassification = 'Tax classification is required';
        if (!formData.termsOfService) stepErrors.termsOfService = 'Terms of service agreement is required';
        if (!formData.privacyPolicy) stepErrors.privacyPolicy = 'Privacy policy agreement is required';
        if (!formData.safetyGuidelines) stepErrors.safetyGuidelines = 'Safety guidelines agreement is required';
        if (!formData.qualityStandards) stepErrors.qualityStandards = 'Quality standards agreement is required';
        if (!formData.finalAcknowledgment) stepErrors.finalAcknowledgment = 'Final acknowledgment is required';
        break;
        
      default:
        break;
    }
    
    // Only set errors if we should (when user has interacted) or always set them when explicitly called from handleNext
    if (shouldSetErrors && (hasUserInteracted || hasAttemptedNext)) {
      setErrors(stepErrors);
    } else if (shouldSetErrors && !hasUserInteracted && !hasAttemptedNext) {
      // Don't set errors on initial load
      setErrors({});
    } else if (shouldSetErrors) {
      setErrors(stepErrors);
    }
    
    // Clear save error if validation passes to ensure clean UI state
    if (Object.keys(stepErrors).length === 0) {
      setSaveError('');
    }
    
    return Object.keys(stepErrors).length === 0;
  }, [formData, hasUserInteracted, hasAttemptedNext]);

  // Load saved data on component mount
  React.useEffect(() => {
    if (user && !isLoaded) {
      loadSavedData();
    } else if (!user) {
      setIsLoaded(true);
    }
  }, [user, isLoaded, loadSavedData]);

  // Real-time validation whenever form data or active step changes
  React.useEffect(() => {
    validateStep(activeStep, true);
  }, [formData, activeStep, validateStep]);

  const handleNext = async () => {
    setHasAttemptedNext(true);
    if (validateStep(activeStep, true)) {
      setSaveError(''); // Clear any previous save errors
      
      // Special handling for step 0: Create user account
      if (activeStep === 0 && !user) {
        setIsSubmitting(true);
        setAccountProcessingStep('Creating account...');
        try {
          // Create user account using AuthContext register function
          const registrationData = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: formData.password,
            phoneNumber: formData.phoneNumber,
            role: 'MAID', // Set role as MAID for onboarding users
            tenantId: process.env.REACT_APP_TENANT_ID || 1
          };
          
          try {
            // Use the AuthContext register function which handles everything properly
            await register(registrationData);
            logger.log('User account created and authenticated successfully');
            setAccountProcessingStep('Account created successfully!');
            setActiveStep(prev => prev + 1);
            setHasAttemptedNext(false); // Reset for new step
            // Scroll to top after step change
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } catch (registrationError) {
            // Check if error is "account already exists"
            const errorMessage = registrationError.response?.data?.error || registrationError.message || '';
            const isAccountExistsError = 
              errorMessage.toLowerCase().includes('already exists') ||
              errorMessage.toLowerCase().includes('already registered') ||
              errorMessage.toLowerCase().includes('duplicate') ||
              errorMessage.toLowerCase().includes('unique constraint') ||
              registrationError.response?.status === 409;
            
            if (isAccountExistsError) {
              logger.log('Account already exists, attempting login...');
              setAccountProcessingStep('Account exists, logging in...');
              
              // Try to login with existing account
              try {
                await login(formData.email, formData.password);
                logger.log('Successfully logged in with existing account');
                setAccountProcessingStep('Login successful!');
                setActiveStep(prev => prev + 1);
                setHasAttemptedNext(false); // Reset for new step
                // Scroll to top after step change
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } catch (loginError) {
                console.error('Login failed for existing account:', loginError);
                setSaveError('An account with this email already exists, but the password is incorrect. Please check your password or use a different email address.');
              }
            } else {
              // Handle other registration errors
              throw registrationError;
            }
          }
          
        } catch (error) {
          console.error('Account creation error:', error);
          setSaveError(error.message || 'Failed to create account. Please try again.');
        } finally {
          setIsSubmitting(false);
          // Clear processing step after a short delay if successful
          if (accountProcessingStep.includes('successful')) {
            setTimeout(() => setAccountProcessingStep(''), 2000);
          } else {
            setAccountProcessingStep('');
          }
        }
        return;
      }
      
      // For other steps: Auto-save progress before moving to next step
      // Only save if validation passes to prevent backend validation errors
      let saveResult = { success: true };
      if (validateStep(activeStep, false)) {
        saveResult = await saveProgress(activeStep);
      } else {
        logger.log('Skipping save progress - validation failed for step:', activeStep);
      }
      
      if (saveResult.success) {
        setActiveStep(prev => prev + 1);
        setHasAttemptedNext(false); // Reset for new step
        // Scroll to top after step change
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Show error but allow user to proceed if they want
        setSaveError(saveResult.error);
        // Still allow progression - just show the error
        setActiveStep(prev => prev + 1);
        setHasAttemptedNext(false); // Reset for new step
        // Scroll to top after step change
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBack = () => {
    setErrors({}); // Clear all errors when navigating backward to ensure fresh validation state
    setSaveError(''); // Clear any save errors as well
    setHasAttemptedNext(false); // Reset attempted state for new step
    setActiveStep(prev => prev - 1);
    // Scroll to top after step change
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep, true)) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Prepare the data for submission to new onboarding API (flat structure)
      const submissionData = {
        ...formData,
        // Ensure maxTravelDistance is always a valid number
        maxTravelDistance: Number(formData.maxTravelDistance) || 15,
        currentStep: activeStep,
        finalAcknowledgment: true, // Mark as complete submission
        signatureDate: new Date().toISOString(),
        // Multi-tenant fields
        tenantId: process.env.REACT_APP_TENANT_ID || 1,
        cityCode: process.env.REACT_APP_CITY_CODE || 'MCO',
        
        // Admin mode: include target maid ID if editing another maid's profile
        ...(targetMaidId && { targetMaidId }),
        
        // Note: fullLegalName, phoneNumber, email will be handled by backend
        // and stored in User table, while other fields go to MaidProfiles table
      };
      
      // Use new maid onboarding endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/maids/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submissionData)
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        const formattedError = formatValidationErrors(responseData);
        throw new Error(formattedError);
      }
      
      if (!responseData.success) {
        const formattedError = formatValidationErrors(responseData);
        throw new Error(formattedError);
      }
      
      logger.log('Onboarding submitted successfully:', responseData);
      
      // Handle success based on mode
      if (isAdminMode) {
        // Admin mode: redirect back to maid management with success message
        navigate('/maid-profile', { 
          state: { 
            success: true,
            message: responseData.message || 'Technician profile updated successfully',
            maidName: targetMaidInfo ? `${targetMaidInfo.firstName} ${targetMaidInfo.lastName}` : 'Technician'
          }
        });
      } else {
        // Regular mode: redirect to success page
        navigate('/onboarding-success', { 
          state: { 
            submissionData: responseData.data,
            message: responseData.message 
          }
        });
      }
      
    } catch (error) {
      console.error('Onboarding submission error:', error);
      setSubmitError(error.message || 'Failed to submit onboarding. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get steps appropriate for current user (authenticated or guest)
  const currentSteps = getStepsForUser(user);
  
  // Calculate the actual step index based on user type
  const getStepIndex = (step, isAuthenticated) => {
    return isAuthenticated ? step - 1 : step;
  };
  
  const stepIndex = getStepIndex(activeStep, !!user);
  const CurrentStepComponent = currentSteps[stepIndex]?.component;
  const isLastStep = stepIndex === currentSteps.length - 1;

  // Show loading while checking for saved data
  if (!isLoaded) {
    return (
      <PageLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="h6">Loading your onboarding progress...</Typography>
        </Box>
      </PageLayout>
    );
  }

  return (
    <ErrorBoundary>
      <PageLayout>
      <Box 
        sx={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          minHeight: '100vh',
          pt: { xs: 12, sm: 14, md: 10 },
          pb: { xs: 4, sm: 6 },
          // Mobile-specific fixes
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            mx: { xs: 1, sm: 2 },
            maxWidth: { xs: '100%', sm: '90%', md: '1200px' },
            width: '100%'
          }}
        >
        {/* Admin Mode Breadcrumbs */}
        {isAdminMode && (
          <Breadcrumbs 
            aria-label="navigation" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            <Link 
              color="inherit" 
              href="/maid-profile" 
              onClick={(e) => {
                e.preventDefault();
                navigate('/maid-profile');
              }}
            >
              Technician Management
            </Link>
            <Typography color="text.primary">
              Edit Profile: {targetMaidInfo ? `${targetMaidInfo.firstName} ${targetMaidInfo.lastName}` : 'Loading...'}
            </Typography>
          </Breadcrumbs>
        )}

        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          align="center" 
          gutterBottom
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            mb: { xs: 2, sm: 3 }
          }}
        >
          {isAdminMode ? 'Edit Technician Profile' : 'Technician Onboarding Application'}
        </Typography>
        <Typography 
          variant="body1" 
          align="center" 
          color="textSecondary" 
          sx={{ 
            mb: { xs: 3, sm: 4 },
            fontSize: { xs: '0.9rem', sm: '1rem' },
            px: { xs: 1, sm: 2 }
          }}
        >
          {isAdminMode 
            ? `Update profile information for ${targetMaidInfo ? `${targetMaidInfo.firstName} ${targetMaidInfo.lastName}` : 'technician'}`
            : 'Complete your professional cleaning service onboarding'
          }
        </Typography>

        {/* Admin Mode Alert */}
        {isAdminMode && targetMaidInfo && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: { xs: 2, sm: 3 },
              mx: { xs: 0, sm: 1 },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              <strong>Admin Mode:</strong> You are editing the profile for {targetMaidInfo.firstName} {targetMaidInfo.lastName} ({targetMaidInfo.email})
            </Typography>
          </Alert>
        )}

          {/* Resume Draft Alert */}
          {user && savedDraftId && lastSaved && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                mx: { xs: 0, sm: 1 },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                <strong>Resuming your application</strong> - Last saved: {lastSaved.toLocaleString()}
              </Typography>
            </Alert>
          )}

          {/* Saving Status */}
          {user && (isSaving || lastSaved) && (
            <Box sx={{ 
              mb: { xs: 1.5, sm: 2 }, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              px: { xs: 1, sm: 0 }
            }}>
              {isSaving ? (
                <Typography 
                  variant="caption" 
                  color="primary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  💾 Saving progress...
                </Typography>
              ) : lastSaved && (
                <Typography 
                  variant="caption" 
                  color="success.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  ✅ Draft saved at {lastSaved.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
          )}

          {/* Progress Stepper */}
          <Stepper 
            activeStep={stepIndex} 
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{ 
              mb: { xs: 3, sm: 4 },
              mx: { xs: -1, sm: 0 },
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: { xs: 500, sm: 400 }
              },
              '& .MuiStepConnector-root': {
                ...(isMobile && { ml: 1, mt: 0.5 })
              }
            }}
          >
            {currentSteps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography 
                    variant="caption"
                    sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      display: { xs: 'block', sm: 'inline' },
                      lineHeight: { xs: 1.2, sm: 1.5 }
                    }}
                  >
                    {isMobile ? step.label.replace(' & ', ' &\n') : step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Progress Bar */}
          <Box sx={{ mb: { xs: 2, sm: 3 }, px: { xs: 0, sm: 1 } }}>
            <LinearProgress 
              variant="determinate" 
              value={((stepIndex + 1) / currentSteps.length) * 100}
              sx={{ 
                height: { xs: 6, sm: 8 }, 
                borderRadius: 4,
                mb: { xs: 1, sm: 1.5 }
              }}
            />
            <Typography 
              variant="caption" 
              color="textSecondary" 
              sx={{ 
                mt: 1, 
                display: 'block',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                textAlign: 'center',
                px: { xs: 1, sm: 0 }
              }}
            >
              Step {stepIndex + 1} of {currentSteps.length}: {currentSteps[stepIndex] && (isMobile 
                ? currentSteps[stepIndex].description.length > 50 
                  ? currentSteps[stepIndex].description.substring(0, 50) + '...'
                  : currentSteps[stepIndex].description
                : currentSteps[stepIndex].description)
              }
            </Typography>
          </Box>

          {/* Error Alerts */}
          {submitError && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                mx: { xs: 0, sm: 1 },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  whiteSpace: 'pre-line' // Preserve line breaks
                }}
              >
                {submitError}
              </Typography>
            </Alert>
          )}
          
          {saveError && (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                mx: { xs: 0, sm: 1 }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  whiteSpace: 'pre-line' // Preserve line breaks
                }}
              >
                <strong>Save Warning:</strong> {saveError}
              </Typography>
              <Typography 
                variant="caption" 
                color="textSecondary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              >
                You can continue, but your progress may not be saved. Please check your connection.
              </Typography>
            </Alert>
          )}

          {/* Success Alert for Account Processing */}
          {accountProcessingStep && accountProcessingStep.includes('successful') && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                mx: { xs: 0, sm: 1 }
              }}
            >
              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                <strong>Success!</strong> {accountProcessingStep} You can now continue with your profile information.
              </Typography>
            </Alert>
          )}

          {/* Current Step Content */}
          <CurrentStepComponent
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />

          {/* Navigation Buttons */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' },
            mt: { xs: 3, sm: 4 },
            gap: { xs: 2, sm: 1 }
          }}>
            <Button
              disabled={activeStep === (user ? 1 : 0)}
              onClick={handleBack}
              variant="outlined"
              size={isMobile ? "medium" : "large"}
              sx={{ 
                order: { xs: 2, sm: 1 },
                minWidth: { xs: 'auto', sm: '120px' },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Back
            </Button>

            {/* Admin Mode Cancel Button */}
            {isAdminMode && (
              <Button
                onClick={() => navigate('/maid-profile')}
                variant="outlined"
                color="secondary"
                size={isMobile ? "medium" : "large"}
                sx={{ 
                  order: { xs: 1, sm: 1 },
                  minWidth: { xs: 'auto', sm: '160px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  ml: { xs: 0, sm: 1 }
                }}
              >
                Return to Technician Management
              </Button>
            )}
            
            <Box sx={{ 
              flex: 1, 
              mx: { xs: 0, sm: 2 },
              order: { xs: 1, sm: 2 }
            }}>
              {Object.keys(errors).length > 0 && (hasUserInteracted || hasAttemptedNext) && (
                <Alert 
                  severity="warning" 
                  size="small"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.5, sm: 1 }
                  }}
                >
                  <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Please fix the errors above before continuing
                  </Typography>
                </Alert>
              )}
            </Box>

            {isLastStep ? (
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                size={isMobile ? "medium" : "large"}
                disabled={isSubmitting || Object.keys(errors).length > 0}
                sx={{ 
                  order: { xs: 3, sm: 3 },
                  minWidth: { xs: 'auto', sm: '180px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  py: { xs: 1, sm: 1.5 }
                }}
              >
                {isSubmitting 
                  ? (isAdminMode ? 'Updating Profile...' : 'Submitting...')
                  : (isAdminMode ? 'Update Profile' : 'Submit Application')
                }
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                variant="contained"
                color="primary"
                size={isMobile ? "medium" : "large"}
                disabled={
                  activeStep === 0 
                    ? (Object.keys(errors).length > 0 || isSaving || isSubmitting)
                    : (Object.keys(errors).length > 0 || isSaving || isSubmitting || !user)
                }
                sx={{ 
                  order: { xs: 3, sm: 3 },
                  minWidth: { xs: 'auto', sm: '200px' },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  py: { xs: 1, sm: 1.5 }
                }}
              >
                {isSubmitting && activeStep === 0 
                  ? accountProcessingStep || 'Creating Account...'
                  : isSaving 
                    ? 'Saving...' 
                    : 'Continue'
                }
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
      
      {/* Debug Panel - Remove this in production */}
      <MobileDebugger enabled={process.env.NODE_ENV === 'development'} />
      </PageLayout>
    </ErrorBoundary>
  );
};

export default MaidOnboardingWizard;